import { ScrollLocker } from "../helpers/scroll-locker";
import { breakpoints } from "../helpers/globals";
import { debounce } from "../helpers/debounce";

/**
 * Elements with data-open-mobile-menu attribute will open the dialog on click
 * Elements with data-close-mobile-menu attribute will close the dialog on click
 *
 * To open the dialog via JS, trigger a custom event "open-mobile-menu"
 * To close the dialog via JS, trigger a custom event "close-mobile-menu"
 */
class MobileMenu {
  #menu;
  #nav;
  #breakpoint = breakpoints.nav.up;
  #mainNavMenu;
  #currentSubNavMenuHeightElement = null;
  #stickyObserver;

  constructor() {
    this.#menu = document.querySelector("[data-mobile-menu]");
    this.#nav = document.querySelector("[data-mobile-nav]");
    this.#mainNavMenu = document.querySelector("[data-mobile-nav-main-height]");
    this.#stickyObserver = document.querySelector(
      "[data-mobile-menu-sticky-observer]",
    );
  }

  initialize() {
    if (!this.#menu) {
      console.error(`Mobile menu not found`);
      return;
    }

    this.#registerEvents();
    this.#observeIfSticky();
  }

  #openMenu() {
    document.body.classList.add("menu-opened");
    ScrollLocker.disable();
  }

  #closeMenu() {
    document.body.classList.remove("menu-opened");
    ScrollLocker.enable();
  }

  #resetMenuOnBreakpoint() {
    if (!document.body.classList.contains("menu-opened")) {
      return;
    }

    // If the menu is opened and breakpoint changes, toggle scrolling
    ScrollLocker.toggle(this.#breakpoint.matches);
  }

  #updateNavHeight() {
    if (this.#breakpoint.matches) {
      return;
    }

    const heightElement =
      this.#currentSubNavMenuHeightElement || this.#mainNavMenu;

    if (!heightElement) {
      return;
    }

    const newHeight = heightElement.scrollHeight + "px";

    if (newHeight) {
      this.#nav.style.height = newHeight;
    }
  }

  #openSubNavMenu(subNavMenu) {
    if (!subNavMenu) {
      return;
    }

    const subNavMenuHeightElement = subNavMenu.querySelector(
      ":scope > [data-mobile-nav-menu-height]",
    );

    subNavMenu.classList.add("is-opened");
    this.#currentSubNavMenuHeightElement = subNavMenuHeightElement;
    this.#updateNavHeight();
  }

  #closeSubNavMenu(subNavMenu) {
    if (!subNavMenu) {
      return;
    }

    subNavMenu.classList.remove("is-opened");
    this.#currentSubNavMenuHeightElement = null;
    this.#updateNavHeight();
  }

  #handleDocumentClick(event) {
    // Open Menu
    if (event.target.hasAttribute("data-open-mobile-menu")) {
      event.preventDefault();
      this.#openMenu();
      return;
    }

    // Close Menu
    if (event.target.hasAttribute("data-close-mobile-menu")) {
      event.preventDefault();
      this.#closeMenu();
      return;
    }

    // Open a Nav Menu
    if (event.target.hasAttribute("data-mobile-nav-open")) {
      const parent = event.target.closest("[data-mobile-nav-parent]");

      if (parent) {
        const subNavMenu = parent.querySelector(
          ":scope >[data-mobile-nav-menu]",
        );

        this.#openSubNavMenu(subNavMenu);
      }

      return;
    }

    // Opened Nav Menu - Back Button
    if (event.target.hasAttribute("data-mobile-nav-back")) {
      const subNavMenu = event.target.closest("[data-mobile-nav-menu]");

      this.#closeSubNavMenu(subNavMenu);
      return;
    }
  }

  #registerEvents() {
    const clickHandler = this.#handleDocumentClick.bind(this);
    const openMenuHandler = this.#openMenu.bind(this);
    const closeMenuHandler = this.#closeMenu.bind(this);
    const resetMenuOnBreakpointHandler = this.#resetMenuOnBreakpoint.bind(this);
    const updateNavHeightHandler = this.#updateNavHeight.bind(this);

    document.addEventListener("click", clickHandler);
    document.addEventListener("open-mobile-menu", openMenuHandler);
    document.addEventListener("close-mobile-menu", closeMenuHandler);
    this.#breakpoint.addEventListener("change", resetMenuOnBreakpointHandler);
    window.addEventListener("load", updateNavHeightHandler);
    window.addEventListener("resize", debounce(updateNavHeightHandler, 100));
  }

  #observeIfSticky() {
    if (!this.#stickyObserver) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        document.body.classList.toggle("menu-scrolled", !entry.isIntersecting);
      });
    });

    observer.observe(this.#stickyObserver);
  }
}

const mobileMenu = new MobileMenu();
mobileMenu.initialize();
